import React, { useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";

interface WSYIWYGEditorProps {
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  handleUpload?: any;
}

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
];

const WSYIWYGEditor: React.FC<WSYIWYGEditorProps> = ({
  value,
  setValue,
  placeholder,
  handleUpload,
}) => {
  const quillRef = useRef<any>();

  const imageHandler = useCallback(() => {
    const editor = quillRef.current.getEditor();
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input?.files?.[0];
      if (file) {
        const res = await handleUpload(file);
        const url = res?.data?.image;
        const cursorPosition = editor.getSelection()?.index || 0;
        editor.insertEmbed(cursorPosition, "image", url);
        const imgElement = editor.container.querySelector(`img[src="${url}"]`);
        if (imgElement) {
          imgElement.setAttribute("alt", file.name);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modules = useMemo(() => {
    const baseToolbar = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ align: [] }],
      ["link"],
    ];

    const imageToolbar = [{ image: null }];

    const toolbar = handleUpload ? [...baseToolbar, imageToolbar] : baseToolbar;

    return {
      toolbar: {
        container: toolbar,
        clipboard: {
          matchVisual: false,
        },
        handlers: {
          image: imageHandler,
        },
      },
    };
  }, [handleUpload, imageHandler]);

  return (
    <section className="w-full bg-white rounded-lg">
      <ReactQuill
        ref={quillRef}
        placeholder={placeholder}
        value={value}
        onChange={setValue}
        modules={modules}
        formats={formats}
      />
    </section>
  );
};

export default WSYIWYGEditor;
